import { Typography as MUITypography, TypographyProps as MUITypographyProps } from '@material-ui/core';
import React from 'react';
import { css, styled } from '~ui-kit/styled-components';
import {
  styledAlignItems,
  StyledAlignItemsProps,
  styledColor,
  StyledColorProps,
  styledDisplay,
  StyledDisplayProps,
  styledFontFamily,
  StyledFontFamilyProps,
  styledFontSize,
  StyledFontSizeProps,
  styledFontWeight,
  StyledFontWeightProps,
  styledLetterSpacing,
  StyledLetterSpacingProps,
  styledLineHeight,
  StyledLineHeightProps,
  styledSpace,
  StyledSpaceProps,
  styledTextAlign,
  StyledTextAlignProps,
  styledWidth,
  StyledWidthProps,
} from '~ui-kit/theme/styled-system';

// @ts-expect-error React.ElementType is missing from the version of @types/react that we use (16.7.7) and upgrading it
// causes other errors (it also doesn't match our React version...).
// To fix this, we need to upgrade and sync our version of React and @types/react
type ElementType = React.ElementType;

/**
 * The generic is used to type MUI Typography's component prop
 * See: https://v4.mui.com/guides/typescript/#usage-of-component-prop
 */
export type TypographyProps<T extends ElementType> = {
  id?: string;
  muiProps?: Omit<MUITypographyProps<T, { component?: T }>, 'color'>;
  noWrap?: boolean;
  component?: T;
  className?: string;
  onClick?: () => void;
  children?: React.ReactNode;
  $blurred?: boolean;
  $blurSize?: number | string;
  $blurColor?: string;
} & StyledColorProps &
  StyledFontSizeProps &
  StyledFontWeightProps &
  StyledFontFamilyProps &
  StyledLetterSpacingProps &
  StyledTextAlignProps &
  StyledSpaceProps &
  StyledLineHeightProps &
  StyledDisplayProps &
  StyledAlignItemsProps &
  StyledWidthProps;

export function Typography<T extends ElementType>({
  muiProps,
  component,
  noWrap = false,
  $blurred = false,
  $blurSize = '30px',
  $blurColor = '#000',
  ...props
}: TypographyProps<T>) {
  return (
    <StyledTypography
      {...muiProps}
      {...props}
      component={component || muiProps?.component}
      $noWrap={noWrap}
      $blurred={$blurred}
      $blurSize={$blurSize}
      $blurColor={$blurColor}
      variant="inherit"
    />
  );
}

interface StyledTypographyProps
  extends TypographyProps<'span'>,
    StyledColorProps,
    StyledFontSizeProps,
    StyledFontWeightProps,
    StyledFontFamilyProps,
    StyledLetterSpacingProps,
    StyledTextAlignProps,
    StyledSpaceProps,
    StyledLineHeightProps,
    StyledDisplayProps,
    StyledAlignItemsProps,
    StyledWidthProps {
  $noWrap?: boolean;
}

const StyledTypography = styled(MUITypography)<StyledTypographyProps>`
  && {
    ${styledFontFamily}
    ${styledFontSize}
    ${styledFontWeight}
    ${styledColor}
    ${styledLetterSpacing}
    ${styledTextAlign}
    ${styledLineHeight}
    ${styledSpace}
    ${styledDisplay}
    ${styledAlignItems}
    ${styledWidth}

    ${({ $noWrap }) =>
      $noWrap &&
      css`
        white-space: nowrap;
      `};

    ${({ $blurred, $blurSize, $blurColor }) =>
      $blurred &&
      css`
        color: transparent;
        text-shadow: 0 0 ${typeof $blurSize === 'number' ? `${$blurSize}px` : $blurSize} ${$blurColor};
        user-select: none;
      `}
  }
`;
